/**
 * Who is on a page right now, published by useCollaborativeEditor and read by
 * the header face-pile.
 *
 * The header sits beside the page content, not above it, so it can't reach the
 * editor's state — same constraint that produced editorActionsRegistry. This
 * is a module store keyed by block id, consumed via useSyncExternalStore.
 */

export interface LivePeer {
  email: string;
  name: string;
  color: string;
}

const EMPTY: LivePeer[] = [];
const entries = new Map<string, { key: string; peers: LivePeer[] }>();
const listeners = new Set<() => void>();

function keyOf(peers: LivePeer[]): string {
  return peers.map((p) => `${p.email}|${p.name}|${p.color}`).join(",");
}

function emit(): void {
  listeners.forEach((listener) => listener());
}

/** No-ops when nothing changed — useSyncExternalStore needs a stable snapshot. */
export function publishLivePeers(blockId: string, peers: LivePeer[]): void {
  if (!blockId) return;
  const key = keyOf(peers);
  if (entries.get(blockId)?.key === key) return;
  entries.set(blockId, { key, peers });
  emit();
}

export function clearLivePeers(blockId: string): void {
  if (!blockId || !entries.has(blockId)) return;
  entries.delete(blockId);
  emit();
}

export function getLivePeers(blockId: string): LivePeer[] {
  return entries.get(blockId)?.peers ?? EMPTY;
}

export function subscribeLivePeers(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
